/**
 * Reading the SMS trail.
 *
 * Everything the platform texts is written to `sms_log`, and since migration
 * 024 the carrier's verdict comes back against the same row. This module is
 * the read side of that — nothing here sends anything.
 *
 * One deliberate behaviour: if migration 024 has not been run, the reply and
 * opt-out tables do not exist and Supabase returns a "relation does not exist"
 * error. That is reported as a `needsMigration` flag rather than as an empty
 * list, because an empty list would read as "nobody has ever replied" when the
 * truth is "we are not recording replies yet".
 */

import { supabase } from '../lib/supabase'

export interface SmsLogRow {
  id: string
  deal_id: string | null
  invoice_id: string | null
  to_number: string
  message: string
  trigger_name: string | null
  status: 'sent' | 'failed' | string
  provider: string | null
  provider_message_id: string | null
  provider_sms_id?: string | null
  sender?: string | null
  reference?: string | null
  delivery_status?: string | null
  delivered_at?: string | null
  delivery_detail?: string | null
  segments?: number | null
  price?: number | null
  error_detail: string | null
  created_at: string
}

export interface SmsReplyRow {
  id: string
  from_number: string
  to_number: string | null
  body: string
  deal_id: string | null
  invoice_id: string | null
  is_opt_out: boolean
  handled_at: string | null
  received_at: string
}

export interface Fetched<T> {
  rows: T[]
  needsMigration: boolean
  error: string | null
}

/** Postgres says this when the table is not there. */
const MISSING_RELATION = /relation .* does not exist|could not find the table/i

export type SentFilter = 'all' | 'failed' | 'undelivered' | 'awaiting'

export async function fetchSmsLog(filter: SentFilter = 'all', limit = 200): Promise<Fetched<SmsLogRow>> {
  let q = supabase.from('sms_log').select('*').order('created_at', { ascending: false }).limit(limit)

  if (filter === 'failed') q = q.eq('status', 'failed')
  if (filter === 'undelivered') q = q.eq('delivery_status', 'undelivered')
  // Accepted by the provider but no receipt yet. Not a failure — just unknown.
  if (filter === 'awaiting') q = q.eq('status', 'sent').is('delivery_status', null)

  const { data, error } = await q
  if (error) {
    return { rows: [], needsMigration: MISSING_RELATION.test(error.message), error: error.message }
  }
  return { rows: (data || []) as SmsLogRow[], needsMigration: false, error: null }
}

/**
 * Every message sent on one deal, oldest last.
 *
 * The deal workspace asks this so the question "was the seller actually told?"
 * can be answered on the deal itself, rather than by going to the SMS log and
 * matching phone numbers by eye.
 */
export async function fetchSmsLogForDeal(dealId: string): Promise<Fetched<SmsLogRow>> {
  const { data, error } = await supabase
    .from('sms_log')
    .select('*')
    .eq('deal_id', dealId)
    .order('created_at', { ascending: false })
  if (error) {
    return { rows: [], needsMigration: MISSING_RELATION.test(error.message), error: error.message }
  }
  return { rows: (data || []) as SmsLogRow[], needsMigration: false, error: null }
}

export async function fetchSmsReplies(limit = 100): Promise<Fetched<SmsReplyRow>> {
  const { data, error } = await supabase
    .from('sms_replies')
    .select('*')
    .order('received_at', { ascending: false })
    .limit(limit)
  if (error) {
    return { rows: [], needsMigration: MISSING_RELATION.test(error.message), error: error.message }
  }
  return { rows: (data || []) as SmsReplyRow[], needsMigration: false, error: null }
}

export async function markReplyHandled(id: string, userId?: string | null): Promise<{ ok: boolean; error?: string }> {
  const { error } = await supabase
    .from('sms_replies')
    .update({ handled_at: new Date().toISOString(), handled_by: userId || null })
    .eq('id', id)
  return error ? { ok: false, error: error.message } : { ok: true }
}

/**
 * What the log adds up to.
 *
 * Deliberately counts "delivered" separately from "sent": the provider
 * accepting a message and the handset receiving it are different facts, and
 * conflating them is how you end up telling a buyer their invoice was sent
 * when it never arrived.
 */
export interface SmsTotals {
  total: number
  failed: number
  delivered: number
  undelivered: number
  awaitingReceipt: number
  spend: number
}

export function summarise(rows: SmsLogRow[]): SmsTotals {
  const t: SmsTotals = { total: rows.length, failed: 0, delivered: 0, undelivered: 0, awaitingReceipt: 0, spend: 0 }
  for (const r of rows) {
    if (r.status === 'failed') t.failed++
    else if (r.delivery_status === 'delivered') t.delivered++
    else if (r.delivery_status === 'undelivered') t.undelivered++
    else t.awaitingReceipt++
    t.spend += Number(r.price || 0)
  }
  return t
}
